"use client";

import styles from "./TemplateDetailPageHeader.module.css";
import { Text } from "@zapier/design-system";
import { useInView } from "react-intersection-observer";
import { useEffect, useState } from "react";
import { getZapBreakdown } from "src/lib/getZapBreakdown";
import {
  type Template,
  TemplateHeroSection,
  TemplateCTAs,
  type ZapBreakdownDetails,
} from "@zapier/templates";
import { useTemplateBookmark } from "src/contexts/TemplateBookmarkContext";
import { useTemplateExecution } from "src/contexts/TemplateExecutionContext";

type TemplateDetailPageHeaderProps = {
  template: Template;
  isLoggedIn: boolean;
  isDraftModeEnabled: boolean;
};

export const TemplateDetailPageHeader = ({
  template,
  isLoggedIn,
  isDraftModeEnabled,
}: TemplateDetailPageHeaderProps) => {
  const [zapBreakdownDetails, setZapBreakdownDetails] =
    useState<ZapBreakdownDetails | null>(template.zapBreakdownDetails ?? null);
  const { ref, inView } = useInView({
    threshold: 0.1,
    initialInView: true,
    fallbackInView: true,
  });
  const {
    createUserTemplateBookmark,
    deleteUserTemplateBookmark,
    bookmarkedTemplates,
    templateBookmarkChanging,
  } = useTemplateBookmark();

  const {
    createTemplate,
    creationScreen,
    error: creationError,
    setError: setCreationError,
  } = useTemplateExecution();

  const isStickyHeader = !inView;

  // Get Zap breakdown details (if this is a Zap template) to display in the header
  useEffect(() => {
    const fetchZapBreakdown = async () => {
      try {
        const zapBreakdownDetails = await getZapBreakdown(template);
        setZapBreakdownDetails(zapBreakdownDetails);
      } catch (error) {
        console.error("Error fetching zap breakdown:", error);
      }
    };

    if (!zapBreakdownDetails) {
      fetchZapBreakdown();
    }
  }, []);

  return (
    <div ref={ref}>
      {isStickyHeader ? (
        <div className={styles.stickyHeader}>
          <div className={styles.content}>
            <Text tag="h1" type="ParagraphHeader3" className={styles.title}>
              {template.name} {isDraftModeEnabled && "(Draft)"}
            </Text>
            <TemplateCTAs
              template={template}
              isLoggedIn={isLoggedIn}
              align="center"
              hasBookmarking={isLoggedIn}
              templateBookmarkActions={{
                onSave: createUserTemplateBookmark,
                onUnsave: deleteUserTemplateBookmark,
                isBookmarked: bookmarkedTemplates?.includes(template.id),
                isLoading: templateBookmarkChanging === template.id,
              }}
              tryTemplateActions={{
                hasCreationScreen: !!creationScreen,
                createTemplate: createTemplate,
                creationError: creationError,
                setCreationError: setCreationError,
              }}
            />
          </div>
        </div>
      ) : (
        <TemplateHeroSection
          template={template}
          isLoggedIn={isLoggedIn}
          isDraftModeEnabled={isDraftModeEnabled}
          zapBreakdownDetails={zapBreakdownDetails}
          tryTemplateActions={{
            hasCreationScreen: !!creationScreen,
            createTemplate: createTemplate,
            creationError: creationError,
            setCreationError: setCreationError,
          }}
          templateBookmarkActions={{
            onSave: createUserTemplateBookmark,
            onUnsave: deleteUserTemplateBookmark,
            isBookmarked: bookmarkedTemplates?.includes(template.id),
            isLoading: templateBookmarkChanging === template.id,
          }}
          hasBookmarking={isLoggedIn}
        />
      )}
    </div>
  );
};
